// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
/**
 * L'EXPORT DE RÉFÉRENCE D'UN REGISTRE (L-1, 06/10/2026 ; monté au socle partagé en L0, 10/10/2026).
 *
 * Un seul export par registre, et il ignore les préférences d'affichage :
 *
 *  · les COLONNES sont celles que l'appelant déclare (jamais l'ordre, le
 *    masquage ni la largeur que la personne a réglés à l'écran) ;
 *  · les LIGNES sont les lignes FILTRÉES — la recherche et les facettes du
 *    registre s'appliquent, et l'en-tête dit lesquelles ;
 *  · la première ligne du fichier porte la date, l'auteur, le registre, le
 *    nombre de lignes et les filtres : une pièce sortie du logiciel se lit sans
 *    lui.
 *
 * Format : séparateur `;` (Excel en configuration française ouvre un fichier à
 * virgules en UNE colonne), BOM UTF-8 (sans lui, « Écouvillon » devient
 * « Ãcouvillon »), guillemets doublés.
 *
 * Module PUR (aucun accès au navigateur hors `telechargerExport`), SANS import :
 * il est recopié dans les trois applications par `shared/sync.sh`. La GED le
 * réexporte sous `lib/export-registre.ts`, et le test `front-export-registre`
 * en rejoue la construction.
 */

/**
 * Une cellule CSV.
 *
 * ⚠️ Une cellule qui commence par `=`, `+`, `-` ou `@` est préfixée d'une
 * apostrophe : sans cela Excel l'interprète comme une FORMULE. Un titre qui
 * commence par un tiret deviendrait une erreur de calcul, et un contenu bien
 * choisi deviendrait une injection.
 */
export function celluleCsv(v: string): string {
  const s = /^[=+\-@]/.test(v) ? `'${v}` : v;
  return `"${s.replace(/"/g, '""')}"`;
}

export interface ColonneExport<L> {
  libelle: string;
  valeur: (ligne: L) => string;
}

const deux = (n: number) => String(n).padStart(2, '0');

/** « jj/mm/aaaa hh:mm », à l'heure LOCALE — le jour et l'heure lus dans le même fuseau. */
export function dateHeureExport(d: Date): string {
  return `${deux(d.getDate())}/${deux(d.getMonth() + 1)}/${d.getFullYear()} ${deux(d.getHours())}:${deux(d.getMinutes())}`;
}

/** `bio24-<registre>-AAAAMMJJ-HHMM.csv`, à l'heure locale. */
export function nomFichierExport(registre: string, quand: Date): string {
  const jour = `${quand.getFullYear()}${deux(quand.getMonth() + 1)}${deux(quand.getDate())}`;
  const heure = `${deux(quand.getHours())}${deux(quand.getMinutes())}`;
  return `bio24-${registre}-${jour}-${heure}.csv`;
}

/** « Exporté le jj/mm/aaaa hh:mm par Prénom Nom · <registre> · <n> lignes · Filtres : … » */
export function enteteExport(o: {
  libelleRegistre: string; auteur: string; nbLignes: number; filtres: string[]; quand: Date;
}): string {
  const n = `${o.nbLignes} ligne${o.nbLignes > 1 ? 's' : ''}`;
  const filtres = o.filtres.length ? o.filtres.join(' ; ') : 'aucun';
  return `Exporté le ${dateHeureExport(o.quand)} par ${o.auteur || 'auteur inconnu'} · ${o.libelleRegistre} · ${n} · Filtres : ${filtres}`;
}

export interface DemandeExport<L> {
  /** Identifiant court du registre, pour le nom du fichier (« nc », « catalogue »). */
  registre: string;
  /** Nom lisible du registre, pour l'en-tête. */
  libelleRegistre: string;
  auteur: string;
  /** Les lignes FILTRÉES du registre. */
  lignes: L[];
  colonnes: ColonneExport<L>[];
  /** Ce qui restreint les lignes, en clair (« Gravité : critique », « Recherche « abc » »). */
  filtres: string[];
}

export function construireExport<L>(o: DemandeExport<L> & { quand: Date }): { nom: string; contenu: string } {
  const entete = celluleCsv(enteteExport({
    libelleRegistre: o.libelleRegistre, auteur: o.auteur, nbLignes: o.lignes.length, filtres: o.filtres, quand: o.quand,
  }));
  const titres = o.colonnes.map((c) => celluleCsv(c.libelle)).join(';');
  const corps = o.lignes.map((l) => o.colonnes.map((c) => celluleCsv(c.valeur(l))).join(';'));
  return {
    nom: nomFichierExport(o.registre, o.quand),
    contenu: `﻿${[entete, titres, ...corps].join('\r\n')}\r\n`,
  };
}

/** Construit l'export et le fait télécharger. Réservé au navigateur. */
export function telechargerExport<L>(o: DemandeExport<L>): void {
  const { nom, contenu } = construireExport<L>({ ...o, quand: new Date() });
  const url = URL.createObjectURL(new Blob([contenu], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nom;
  a.click();
  URL.revokeObjectURL(url);
}
